import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../../services/auth.service";
import ComplaintService from "../../services/complaint.service";
import ComplaintDetailsModal from "../../components/ComplaintDetailsModal";
import UpdateStatusModal from "../../components/UpdateStatusModal";
import { Complaint, StatusUpdate } from "../../interfaces/Complaint";
import AdminHeader from "../../components/AdminHeader";
import { string } from "yup";

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(
    null
  );
  const [selectedComplaintId, setSelectedComplaintId] = useState<string | null>(
    null
  );
  const [complaintDetails, setComplaintDetails] = useState<Complaint | null>(
    null
  );
  const [search, setSearch] = useState("");
  const [showDetailView, setShowDetailView] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState<StatusUpdate>({
    update_text: "",
    updated_status: "Pending",
  });
  const [filter, setFilter] = useState("all");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(20); // Items per page

  const [refreshFlag, setRefreshFlag] = useState(false);

  // Set document title
  useEffect(() => {
    document.title = "Admin Dashboard - UoR, FoE MRS";
    fetchComplaints();
  }, []);

  // Refetch complaints when search, filter, or page changes
  useEffect(() => {
    fetchComplaints();
  }, [search, filter, currentPage, refreshFlag]);

  // Fetch detailed complaint information when a complaint is selected for viewing
  useEffect(() => {
    if (selectedComplaintId) {
      fetchComplaintDetails(selectedComplaintId);
    }
  }, [selectedComplaintId]);

  const fetchComplaintDetails = async (id: string) => {
    try {
      const details = await ComplaintService.getComplaintDetails(id);
      setComplaintDetails(details);
      setLoading(false);
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Failed to fetch complaint details"
      );
      setLoading(false);
    }
  };

  const fetchComplaints = async () => {
    setLoading(true);
    setError(null);
    try {
      // Convert filter to status number for API
      let status = "";
      if (filter === "Pending") status = "1";
      else if (filter === "Ongoing") status = "2";
      else if (filter === "Completed") status = "3";

      const response = await ComplaintService.getAllComplaints(
        search,
        currentPage,
        limit,
        status
      );

      // Enhance the complaints with user full names if missing
      const enhancedComplaints = response.data.map((complaint: any) => {
        // If first_name and last_name are missing, add placeholders
        if (!complaint.first_name) {
          return {
            ...complaint,
            first_name: "User",
            last_name: complaint.user_id,
          };
        }
        return complaint;
      });

      setComplaints(enhancedComplaints);
      setTotalPages(response.totalPages);
      setTotalCount(response.totalCount);
      setCurrentPage(response.currentPage);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to fetch complaints");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
    fetchComplaints();
  };

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSelectComplaint = (complaint: Complaint) => {
    setSelectedComplaint(complaint);
    console.log("Selected Complaint 1:", complaint);
    setStatusUpdate({
      update_text: "",
      updated_status: complaint.status || "Pending",
    });
  };

  const viewComplaintDetails = (id: string) => {
    setSelectedComplaintId(id);
    setShowDetailView(true);
  };

  const closeDetailView = () => {
    setShowDetailView(false);
    setComplaintDetails(null);
    setSelectedComplaintId(null);
  };

  const printComplaintDetails = () => {
    if (!complaintDetails) return;

    // Create a new window for printing
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Please allow pop-ups to print complaint details");
      return;
    }

    // Import and use the print template
    import("../../utils/printTemplate")
      .then(({ generateComplaintPrintTemplate }) => {
        const printContent = generateComplaintPrintTemplate(complaintDetails);
        printWindow.document.open();
        printWindow.document.write(printContent);
        printWindow.document.close();
      })
      .catch((error) => {
        console.error("Error loading print template:", error);
        alert("Error loading print template");
      });
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusUpdate({
      ...statusUpdate,
      updated_status: e.target.value as "Pending" | "Ongoing" | "Completed",
    });
  };

  const handleUpdateTextChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setStatusUpdate({
      ...statusUpdate,
      update_text: e.target.value,
    });
  };

  const handleSubmitStatusUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedComplaint) return;

    const complaintId = String(
      selectedComplaint.id || selectedComplaint.complain_id || ""
    );

    if (!complaintId) {
      setError("Invalid complaint identifier. Please reopen the complaint.");
      return;
    }

    if (!statusUpdate.update_text.trim()) {
      setError("Please provide update notes");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await ComplaintService.updateComplaintStatus(
        complaintId,
        statusUpdate.updated_status,
        statusUpdate.update_text
      );

      // Refresh the complaint list
      await fetchComplaints();

      // Reset form and selection
      setStatusUpdate({
        update_text: "",
        updated_status: "Pending",
      });
      setSelectedComplaint(null);

      // Show success message
      const successEl = document.createElement("div");
      successEl.className =
        "fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded shadow-lg z-50";
      successEl.innerHTML = `
        <div class="flex items-center">
          <span class="text-sm font-medium">Success! Status updated successfully.</span>
          <button type="button" class="ml-4 text-green-700 hover:text-green-900" onclick="this.parentElement.parentElement.remove()">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
            </svg>
          </button>
        </div>
      `;

      document.body.appendChild(successEl);

      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        if (successEl.parentNode) {
          successEl.remove();
        }
      }, 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to update status.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Get status counts for filter buttons (you might need to make separate API calls for counts)
  const getStatusCount = (status: string) => {
    // This would ideally come from a separate API endpoint for counts
    // For now, showing the current page results
    if (status === "all") return totalCount;
    return complaints.filter((c) => c.status === status).length;
  };

  // Pagination component
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const showPages = 5; // Number of page buttons to show
    const startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    const endPage = Math.min(totalPages, startPage + showPages - 1);

    // Previous button
    pages.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`px-3 py-2 text-sm font-medium rounded-md ${
          currentPage === 1
            ? "text-slate-400 cursor-not-allowed"
            : "text-slate-700 hover:bg-slate-100"
        }`}
      >
        Previous
      </button>
    );

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-2 text-sm font-medium rounded-md ${
            i === currentPage
              ? "bg-blue-600 text-white"
              : "text-slate-700 hover:bg-slate-100"
          }`}
        >
          {i}
        </button>
      );
    }

    // Next button
    pages.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`px-3 py-2 text-sm font-medium rounded-md ${
          currentPage === totalPages
            ? "text-slate-400 cursor-not-allowed"
            : "text-slate-700 hover:bg-slate-100"
        }`}
      >
        Next
      </button>
    );

    return (
      <div className="flex items-center justify-between mt-6">
        <div className="text-sm text-slate-600">
          Showing {(currentPage - 1) * limit + 1} to{" "}
          {Math.min(currentPage * limit, totalCount)} of {totalCount} results
        </div>
        <div className="flex items-center space-x-1">{pages}</div>
      </div>
    );
  };

  console.log("all", complaints);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Professional Header */}
      <AdminHeader user={user} />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Dashboard Header */}
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">
                  Admin Dashboard
                </h2>
                <p className="text-slate-600 mt-1">
                  Manage and monitor all maintenance requests
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <div className="bg-blue-50 px-3 py-2 rounded-lg">
                  <span className="text-blue-700 font-semibold text-sm">
                    Total: {totalCount}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="px-6 py-6 bg-white border-b border-slate-200">
            {/* Search Bar */}
            <div className="mb-6 ">
              <form onSubmit={handleSearch} className="relative">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-slate-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    className="block w-full pl-10 pr-12 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2  text-sm placeholder-slate-500"
                    placeholder="Search by subject, description, status, or user name..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    <button
                      type="submit"
                      className="mr-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                    >
                      Search
                    </button>
                  </div>
                </div>
              </form>
            </div>

            {/* Filter Buttons */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-1 bg-slate-100 p-1 rounded-lg">
                {[
                  {
                    key: "all",
                    label: "All Requests",
                    count: totalCount,
                  },
                  {
                    key: "Pending",
                    label: "Pending",
                    count: getStatusCount("Pending"),
                  },
                  {
                    key: "Ongoing",
                    label: "Ongoing",
                    count: getStatusCount("Ongoing"),
                  },
                  {
                    key: "Completed",
                    label: "Completed",
                    count: getStatusCount("Completed"),
                  },
                ].map((filterOption) => (
                  <button
                    key={filterOption.key}
                    onClick={() => handleFilterChange(filterOption.key)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                      filter === filterOption.key
                        ? filterOption.key === "all"
                          ? "bg-blue-600 text-white shadow-sm"
                          : filterOption.key === "Pending"
                          ? "bg-amber-500 text-white shadow-sm"
                          : filterOption.key === "Ongoing"
                          ? "bg-blue-500 text-white shadow-sm"
                          : "bg-emerald-600 text-white shadow-sm"
                        : "text-slate-700 hover:bg-white hover:shadow-sm"
                    }`}
                  >
                    {filterOption.label}
                    <span
                      className={`hidden ml-2 px-2 py-0.5 text-xs rounded-full ${
                        filter === filterOption.key
                          ? "bg-white bg-opacity-25"
                          : "bg-slate-200"
                      }`}
                    >
                      {filterOption.count}
                    </span>
                  </button>
                ))}
              </div>

              <button
                onClick={() => {
                  setSearch("");
                  setFilter("all");
                  setCurrentPage(1);
                  fetchComplaints();
                }}
                className="inline-flex items-center px-4 py-2 border border-slate-300 text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Refresh
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {/* Error Message */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg
                    className="h-5 w-5 text-red-400 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-red-700 text-sm font-medium">
                    {error}
                  </span>
                  <button
                    onClick={() => setError(null)}
                    className="ml-auto text-red-400 hover:text-red-600"
                  >
                    <svg
                      className="h-5 w-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent mr-3"></div>
                  <span className="text-slate-600 font-medium">
                    Loading complaints...
                  </span>
                </div>
              </div>
            ) : (
              /* Complaints Table */
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                          Request ID
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                          Subject
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                          Authorized
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                          Date Created
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {complaints.length > 0 ? (
                        complaints.map((complaint, index) => (
                          <tr
                            key={complaint.id}
                            className="hover:bg-slate-50 transition-colors duration-150"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <span className="text-sm font-mono text-slate-600 bg-slate-100 px-2 py-1 rounded">
                                  #{complaint.id}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-8 w-8">
                                  <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center">
                                    <span className="text-xs font-medium text-slate-600">
                                      {(
                                        complaint.first_name?.[0] || "U"
                                      ).toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                                <div className="ml-3">
                                  <p className="text-sm font-medium text-slate-900">
                                    {complaint.first_name} {complaint.last_name}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-slate-900 font-medium max-w-xs truncate">
                                {complaint.subject.length > 20
                                  ? `${complaint.subject.slice(0, 20)}...`
                                  : complaint.subject}
                              </div>
                              {complaint.category && (
                                <div className="text-xs text-slate-500 mt-1">
                                  {complaint.category}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  complaint.status === "Pending"
                                    ? "bg-amber-100 text-amber-800 border border-amber-200"
                                    : complaint.status === "Ongoing"
                                    ? "bg-blue-100 text-blue-800 border border-blue-200"
                                    : "bg-emerald-100 text-emerald-800 border border-emerald-200"
                                }`}
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                                    complaint.status === "Pending"
                                      ? "bg-amber-400"
                                      : complaint.status === "Ongoing"
                                      ? "bg-blue-400"
                                      : "bg-emerald-400"
                                  }`}
                                ></span>
                                {complaint.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div
                                className={`px-3 py-1.5 text-xs font-medium rounded-full w-fit ${
                                  complaint.isAuthorized == "1"
                                    ? "bg-emerald-100 text-emerald-700"
                                    : "bg-rose-100 text-rose-700"
                                }`}
                              >
                                {complaint.isAuthorized == "1" ? "Yes" : "No"}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                              {new Date(
                                complaint.created_at
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() =>
                                    handleSelectComplaint(complaint)
                                  }
                                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                >
                                  Update
                                </button>
                                <button
                                  onClick={() =>
                                    viewComplaintDetails(complaint.id)
                                  }
                                  className="inline-flex items-center px-3 py-1.5 border border-slate-300 text-xs font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                >
                                  Details
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="px-6 py-16 text-center">
                            <div className="flex flex-col items-center">
                              <svg
                                className="h-12 w-12 text-slate-400 mb-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                              <h3 className="text-sm font-medium text-slate-900 mb-1">
                                No requests found
                              </h3>
                              <p className="text-sm text-slate-500">
                                {search || filter !== "all"
                                  ? "Try adjusting your search or filter criteria"
                                  : "No maintenance requests have been submitted yet"}
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {renderPagination()}
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        {showDetailView && selectedComplaintId && (
          <ComplaintDetailsModal
            complaintDetails={complaintDetails}
            loading={loading}
            onClose={closeDetailView}
            onPrint={printComplaintDetails}
            onUpdateStatus={(complaint) => {
              handleSelectComplaint(complaint);
              setShowDetailView(false);
            }}
          />
        )}

        {selectedComplaint && (
          <UpdateStatusModal
            selectedComplaint={selectedComplaint}
            statusUpdate={statusUpdate}
            loading={loading}
            onClose={() => setSelectedComplaint(null)}
            onStatusChange={handleStatusChange}
            onUpdateTextChange={handleUpdateTextChange}
            onSubmit={handleSubmitStatusUpdate}
            setRefreshFlag={setRefreshFlag}
          />
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
